import React, {useEffect, useMemo, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {RSS_GET_ALL_POSTS_URL} from '../constants';
import {resolvePostImageSrc} from '../posts/imageUrl';

type BlogPost = {
  id: string;
  author?: string;
  title: string;
  description: string;
  pubDate: string;
  image: string;
};

const fmtDateTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const BlogDetail = () => {
  const navigate = useNavigate();
  const params = useParams<{id: string}>();
  const id = (params.id || '').trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(RSS_GET_ALL_POSTS_URL);
        const text = await res.text();
        if (!res.ok) {
          if (!cancelled) setError(text.slice(0, 200) || `Request failed (${res.status})`);
          return;
        }
        const json = JSON.parse(text) as {data?: Array<Record<string, unknown>>};
        const rows = Array.isArray(json.data) ? json.data : [];
        const mapped: BlogPost[] = rows.map((row) => ({
          id: String(row.id ?? ''),
          author: typeof row.author === 'string' ? row.author : undefined,
          title: typeof row.title === 'string' ? row.title : '',
          description: typeof row.description === 'string' ? row.description : '',
          pubDate: typeof row.pubDate === 'string' ? row.pubDate : '',
          image: resolvePostImageSrc(typeof row.image === 'string' ? row.image : ''),
        }));
        if (!cancelled) setPosts(mapped);
      } catch {
        if (!cancelled) setError('Network error. Could not load blog post.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const post = useMemo(() => posts.find((p) => p.id === id), [posts, id]);

  return (
    <div className="posts-page">
      <div className="posts-inner">
        <header className="posts-top">
          <button type="button" className="posts-back" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <Link to="/" className="posts-home-link">
            CriticalMinerals.News
          </Link>
          <Link to="/blog" className="posts-add-btn" style={{textDecoration: 'none'}}>
            Blog
          </Link>
        </header>

        {error ? <p className="posts-delete-banner">{error}</p> : null}

        {loading ? (
          <p className="posts-empty">Loading…</p>
        ) : !post ? (
          <div>
            <h1 className="posts-title">Post not found</h1>
            <p className="posts-delete-banner">This blog post does not exist (or was deleted).</p>
          </div>
        ) : (
          <article style={{maxWidth: 860}}>
            <h1 className="posts-title" style={{marginBottom: 10}}>
              {post.title || 'Untitled'}
            </h1>
            <div style={{display: 'flex', gap: 10, flexWrap: 'wrap', color: 'var(--text3)', fontSize: 12}}>
              {post.author ? <span>By {post.author}</span> : null}
              <span>{fmtDateTime(post.pubDate)}</span>
            </div>

            {post.image ? (
              <div style={{marginTop: 14}}>
                <img
                  src={resolvePostImageSrc(post.image)}
                  alt=""
                  style={{width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 12}}
                  loading="lazy"
                />
              </div>
            ) : null}

            <div style={{marginTop: 14, fontSize: 15, lineHeight: 1.6, color: 'var(--text)'}}>
              {(post.description || '').split('\n').map((line, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <p key={i} style={{margin: '0 0 10px'}}>
                  {line}
                </p>
              ))}
            </div>
          </article>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;

